import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Diamond, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    } else if (!sessionId) {
      setStatus('error');
    }
  }, [searchParams, token]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const res = await fetch(`${API}/api/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired' || data.status === 'failed') {
          setStatus('error');
          return;
        }
      }

      // Continue polling if still processing
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment:', err);
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  };

  const handleContinue = () => {
    navigate('/chat');
  };

  return (
    <div className="min-h-screen bg-[#030305] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#0A0A0F] border border-white/10 rounded-3xl p-8 text-center"
      >
        {status === 'loading' && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#00F0FF]/10 flex items-center justify-center"
            >
              <Loader2 className="w-10 h-10 text-[#00F0FF]" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 10 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center"
            >
              <CheckCircle className="w-10 h-10 text-green-500" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-gray-400 mb-6">Your diamonds have been added to your account.</p>
            
            {paymentData && (
              <div className="bg-[#0F0F16] rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold text-[#00F0FF]">
                  <Diamond className="w-6 h-6" />
                  <span>+{paymentData.metadata?.diamonds || '???'}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Amount: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              </div>
            )}
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleContinue}
              className="w-full py-3 bg-gradient-to-r from-[#00F0FF] to-[#00C8FF] text-black font-semibold rounded-xl"
            >
              Continue to App
            </motion.button>
          </>
        )}

        {status === 'error' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 10 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FF2A2A]/10 flex items-center justify-center"
            >
              <XCircle className="w-10 h-10 text-[#FF2A2A]" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Issue</h1>
            <p className="text-gray-400 mb-6">
              We couldn't confirm your payment. If you were charged, your diamonds will be added shortly.
            </p>
            
            <div className="space-y-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleContinue}
                className="w-full py-3 bg-white/10 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Return to App
              </motion.button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
